// @ts-check
if (!customElements.get("ff-slider")) {
  class FfSlider extends HTMLElement {
    constructor() {
      super();

      /**
       * @type {HTMLElement}
       */
      this.ffSlider = (() => {
        /**
         * @type {HTMLElement | null}
         *`
         */
        const ffSlider = this.querySelector(
          this.dataset.ffSwiperSelector || ""
        );

        if (ffSlider) {
          return ffSlider;
        } else {
          throw new Error(
            `There wasn't found an element with the selector: '${this.dataset.ffSwiperSelector}, which is needed for the creation of the swiper instance.`
          );
        }
      })();

      this.ffMobileItemsPerViewAuto = this.dataset.hasOwnProperty(
        "ffMobileItemsPerViewAuto"
      );
      this.ffDesktopItemsPerViewAuto = this.dataset.hasOwnProperty(
        "ffDesktopItemsPerViewAuto"
      );

      /**
       * @type {NodeListOf<HTMLElement>}
       */
      this.ffSlides = this.querySelectorAll(".swiper-slide");

      this.ffMobileBreakpoint = this.dataset.ffMobileBreakpoint;
      if (this.ffMobileItemsPerViewAuto) {
        this.ffMobileItemsPerView = "auto";
      } else {
        this.ffMobileItemsPerView = this.dataset.ffMobileItemsPerView || "1.5";
      }
      if (this.ffDesktopItemsPerViewAuto) {
        this.ffDesktopItemsPerView = "auto";
      } else {
        this.ffDesktopItemsPerView = this.dataset.ffDesktopItemsPerView || "4";
      }
      this.ffLeftArrowClass = this.dataset.ffLeftArrowClass;
      this.ffRightArrowClass = this.dataset.ffRightArrowClass;

      this.ffAutoplay = !!this.dataset.ffAutoplay;

      // pagination styles
      this.ffDotsClass = this.dataset.ffDotsClass;
      this.ffDotClass = this.dataset.ffDotClass;
      this.ffDotActiveClass = this.dataset.ffDotActiveClass;

      this.ffDisabledArrowClass = this.dataset.ffDisabledArrowClass;

      this.ffMobileSlidesGap = this.dataset.ffMobileSlidesGap || "10";
      this.ffDesktopSlidesGap = this.dataset.ffDesktopSlidesGap || "14";

      this.ffInit();
    }

    ffRestrainArrowsDisplay() {
      /**
       * @type {HTMLButtonElement | null}
       */
      const ffLeftArrow = this.querySelector("." + this.ffLeftArrowClass);
      /**
       * @type {HTMLButtonElement | null}
       */
      const ffRightArrow = this.querySelector("." + this.ffRightArrowClass);

      if (!ffLeftArrow && !ffRightArrow) return;

      if (
        window.matchMedia(`(max-width:${this.ffMobileBreakpoint}px)`).matches
      ) {
        if (this.ffMobileItemsPerViewAuto) {
          if (ffLeftArrow?.disabled && ffRightArrow?.disabled) {
            this.classList.remove("ff-slider-with-arrows--mobile");
            this.classList.remove("ff-slider-with-dots--mobile");
          }
        } else if (
          this.ffSlides.length <= Number.parseFloat(this.ffMobileItemsPerView)
        ) {
          this.classList.remove("ff-slider-with-arrows--mobile");
          this.classList.remove("ff-slider-with-dots--mobile");
        }
      } else {
        if (this.ffDesktopItemsPerViewAuto) {
          if (ffLeftArrow?.disabled && ffRightArrow?.disabled) {
            this.classList.remove("ff-slider-with-arrows--desktop");
            this.classList.remove("ff-slider-with-dots--desktop");
          }
        } else if (
          this.ffSlides.length <= Number.parseFloat(this.ffDesktopItemsPerView)
        ) {
          this.classList.remove("ff-slider-with-arrows--desktop");
          this.classList.remove("ff-slider-with-dots--desktop");
        }
      }
    }

    ffResizeSlides() {
      let highestHeight = 0;

      this.ffSlides.forEach(({ clientHeight }) => {
        if (clientHeight > highestHeight) {
          highestHeight = clientHeight;
        }
      });

      this.ffSlides.forEach((slide) => {
        slide.style.height = `${highestHeight}px`;
      });
    }

    async ffInit() {
      const swiperParams = {
        mousewheel: false,
        spaceBetween: this.ffMobileSlidesGap,
        slidesPerView: this.ffMobileItemsPerView,
        autoplay: true,
      };

      if (this.ffMobileBreakpoint) {
        //breakpoint to use desktop settings
        swiperParams.breakpoints = {
          [this.ffMobileBreakpoint]: {
            slidesPerView: this.ffDesktopItemsPerView,
            spaceBetween: this.ffDesktopSlidesGap,
          },
        };

        if (this.dataset.ffDesktopAutoplay) {
          swiperParams.breakpoints[this.ffMobileBreakpoint].autoplay = {
            delay: this.dataset.ffDesktopAutoplayDelay,
            disabledOnInteraction: true,
          };
        } else {
          swiperParams.breakpoints[this.ffMobileBreakpoint].autoplay = false;
        }
      }

      if (this.dataset.ffMobileAutoplay) {
        swiperParams.breakpoints[100] = {
          autoplay: {
            delay: this.dataset.ffMobileAutoplayDelay,
            disabledOnInteraction: true,
          },
        };
      } else {
        swiperParams.breakpoints[100] = {
          autoplay: false,
        };
      }

      if (this.ffDotsClass) {
        swiperParams.pagination = {
          el: `.${this.ffDotsClass}`,
          bulletClass: `${this.ffDotClass}`,
          bulletActiveClass: `${this.ffDotActiveClass}`,
          type: "bullets",
          clickable: true,
        };
      }

      if (this.ffLeftArrowClass && this.ffRightArrowClass) {
        swiperParams.navigation = {
          prevEl: `.${this.ffLeftArrowClass}`,
          nextEl: `.${this.ffRightArrowClass}`,
          disabledClass: `${this.ffDisabledArrowClass}`,
        };
      }

      swiperParams.on = {
        init: () => {
          this.ffShowContent();
        },
      };

      this.swiper = new Swiper(this.dataset.ffSwiperSelector, swiperParams);

      this.ffRestrainArrowsDisplay();

      window.removeEventListener("resize", this.ffResizeHandler);
      window.addEventListener("resize", this.ffResizeHandler);
    }

    ffHideContent() {
      //This is done so styles applied by classes won't show the loading content.
      this.style.opacity = "0";
    }

    ffShowContent() {
      if (this.dataset.ffResizeSlides) {
        this.ffResizeSlides();
      }
      //This is done so the element direct style is removed, and classes can rule again.
      this.classList.remove("ff-slider-uninitialized");
      this.style.opacity = "";
    }

    ffShopifySectionLoadHandler() {
      if (this.dataset.ffResizeSlides) {
        this.ffSlides.forEach((slide) => {
          slide.style.height = "";
        });

        setTimeout(() => {
          this.ffResizeSlides();
        }, 200);
      }
    }

    ffResizeHandler = () => {
      this.ffInit().then(() => this.ffShopifySectionLoadHandler());
    };
  }

  customElements.define("ff-slider", FfSlider);
}

if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", () => {
    requestAnimationFrame(() => {
      document.querySelectorAll("ff-slider").forEach((slider) => {
        slider.ffShopifySectionLoadHandler();
      });
    });
  });
}
